// deployment_class: customer_env_allowed
/**
 * CEO redesign — the dashboard (Increment 1: shell + five-module Overview).
 *
 * One template renders every module from {@link MODULES} (build-once → every
 * module inherits). Rail switches modules (Jason's order); tabs switch the
 * domain layer; stream cards are connected-nav into their module. The look is
 * the validated new design (white / Inter / Kathedral teal), scoped under
 * `.kdash`. Replaces the old iframe'd HTML demo as the authenticated home.
 *
 * Novello-clean by construction: the ONLY interactive elements are the rail
 * nav, the tabs, and the connected-nav stream cards. Everything else is
 * presentational (no cursor:pointer, no dead affordances). Drills, workflows,
 * the project view + Data Ticker, and the live Ask-Kathedral arrive in later
 * increments and are wired then.
 */
import { useState, useEffect, useRef, type KeyboardEvent, type RefObject } from 'react';
import { useLayerHistory } from './useLayerHistory';
import { MODULES, WORKFLOWS, TAB_PANELS, type ModuleKey, type ModuleDef, type CardDef, type Workflow, type TabPanel, type TabRow } from './dashboard-data';
import { useDashboardContent, useDeals, EMPTY_MODULE_CONTENT } from './dashboard-source';
import { Drill, needDrill, kpiDrill, cardDrill, workflowDrill, tickDrill, rowDrill, messageDrill, milestoneDrill, type DrillDef } from './Drill';
import { ProjectView } from './ProjectView';
import { ProjectGrid } from './project-visual';
import { PHASES } from './project-data';
import { SUGGESTED } from './ask';
import { askKathedral, buildAskBody, type AskRequestBody } from '../shell/ai/useAskKathedral';
import { classifyAskFailure } from '../shell/ai/ask-failure';
import { RichText } from '../shared/ui/RichText';
import { tabPurpose } from './tab-purpose';
import { useAuth } from '../shell/auth/useAuth';
import { EditBar, RemovedTray, isDeveloper } from './TemplateEditor';
import {
  hideModule, showModule, moveModule,
  showElement, showTab,
  visibleModules, elementVisible, tabsOf,
  placeElement, unplaceElement, elementsOf,
  isPermanentModule, isPermanentElement,
} from './template-state';
import { useTemplateStore, freshTemplate } from './template-store';
import { ImportDataTab } from './ImportDataTab';
import { TemplatePackage } from './TemplatePackage';
import {
  TemplateDndProvider,
  DraggablePlaced,
  DraggableModule,
  PackageDropZone,
  type DragPayload,
  type DropTarget,
} from './TemplateDnd';
import './dashboard.css';

/** Accessible clickable props (Enter/Space activate) for non-button elements. */
function clickable(onClick: () => void): {
  role: 'button'; tabIndex: 0; onClick: () => void; onKeyDown: (e: KeyboardEvent) => void;
} {
  return {
    role: 'button', tabIndex: 0, onClick,
    onKeyDown: (e: KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } },
  };
}

/** A pinned shortcut (a module, a project, or ANY card — bug_UI_1i pin-on-all).
 *  For kind 'card', `key` is the card's home moduleKey (clicking returns there). */
interface Pin { id: string; label: string; kind: 'module' | 'project' | 'card' | 'item'; key: string; tab?: string; }

const PIN_KINDS = new Set(['module', 'project', 'card', 'item']);

/** Is this really a Pin, or just something that parsed? */
function isPin(v: unknown): v is Pin {
  if (typeof v !== 'object' || v === null) return false;
  const p = v as Record<string, unknown>;
  return (
    typeof p['id'] === 'string' &&
    typeof p['label'] === 'string' &&
    typeof p['key'] === 'string' &&
    typeof p['kind'] === 'string' &&
    PIN_KINDS.has(p['kind'])
  );
}

/**
 * Read the pins someone saved, and survive whatever is actually there.
 *
 * Exported so it can be tested against the shapes a browser really holds — the
 * class of failure that has no coverage unless a test writes storage first.
 */
export function loadPins(): Pin[] {
  try {
    const raw: unknown = JSON.parse(localStorage.getItem('kd-pins') ?? '[]');
    if (!Array.isArray(raw)) return [];
    return raw.filter(isPin);
  } catch {
    return [];
  }
}

/**
 * ── THE BARE TEMPLATE (E3 2026-08-29) ──────────────────────────────────────
 * E3: *"let's clear our UI of reading data… the modules should be just bare
 * bones, and we can start from scratch"* — and on seeing the result:
 * *"this is essentially what a template should look like and start from."*
 *
 * So this IS the template: the module skeleton with nothing in it. Rail,
 * eyebrow, title and tabs are the container; every figure arrives from a source
 * or does not appear at all.
 *
 * What this empties was 22 KPI values, five apex narratives, three "what needs
 * you" items, every card, every workflow row and every tab panel — all string
 * literals in `dashboard-data.ts`, written 2026-07-01. The dashboard made ZERO
 * API calls for business data: it was not reading from the wrong place, it was
 * not reading at all.
 *
 * NOTHING IS DELETED and nothing is unmounted. `dashboard-data.ts`,
 * `project-data.ts`, `WORKFLOWS` and `TAB_PANELS` are untouched; every
 * component that renders them is still here and still referenced. Only the DATA
 * handed to them is empty — so this is one boolean, both ways. A container with
 * nothing in it should still be the same container.
 */
// The bare template is no longer a constant. A module is bare because NOTHING
// SUPPLIED CONTENT FOR IT — see `dashboard-source.tsx`. Same screen, honest
// reason: empty is now a consequence rather than an assertion, which is what
// lets a test inject data the code has never seen and lets CSA supply the real
// thing later through the identical socket.

/**
 * What an empty tab says about itself.
 *
 * E3 2026-08-29, on a tab that read only "this domain layer is part of the
 * Executive module": *"let's expand on that in terms of what the tab is for and
 * how it's supposed to display data and how it relates to all other objects."*
 *
 * So the bare template documents itself: purpose, the shape the data will take
 * and where it comes from, and how this tab's objects connect to the rest. A
 * tab whose source does not exist says THAT, rather than naming a table nobody
 * ever built.
 */
function TabSummary({ moduleKey, tab, label }: {
  moduleKey: ModuleKey; tab: string; label: string;
}): JSX.Element {
  const p = tabPurpose(moduleKey, tab);
  if (!p) {
    return (
      <div className="kd-note" style={{ marginTop: 24 }}>
        <b>{tab}</b> — part of the {label} module. No purpose has been written for this tab yet.
      </div>
    );
  }
  // No status badge. E3 2026-08-29: "we don't need the little comment that says
  // 'not built yet' — that doesn't make any sense to have." What a tab is FOR
  // does not change with whether it is wired; a badge only invited the reader to
  // discount the description beneath it.
  return (
    <div className="kd-tabsum" style={{ marginTop: 24 }}>
      <div className="kd-tabsum-h">
        <h2>{tab}</h2>
      </div>
      <p className="kd-tabsum-purpose">{p.purpose}</p>
      <dl className="kd-tabsum-dl">
        <dt>What it shows</dt>
        <dd>{p.displays}</dd>
        <dt>How it relates</dt>
        <dd>{p.relates}</dd>
      </dl>
    </div>
  );
}

/** The module as the template hands it over: its shape, without its content. */
function Ring({ v, lbl }: { v: number; lbl?: string }): JSX.Element {
  const r = 44, c = 2 * Math.PI * r, off = c * (1 - v / 100);
  return (
    <div className="kd-ring">
      <svg width="104" height="104" viewBox="0 0 104 104" role="img" aria-label={`${v}% ${lbl ?? ''}`}>
        <circle cx="52" cy="52" r={r} fill="none" stroke="#eee9df" strokeWidth="11" />
        <circle cx="52" cy="52" r={r} fill="none" stroke="#0e7c86" strokeWidth="11" strokeLinecap="round"
          strokeDasharray={c.toFixed(1)} strokeDashoffset={off.toFixed(1)} transform="rotate(-90 52 52)" />
        <text x="52" y="59" textAnchor="middle" style={{ fontWeight: 700, fontSize: 26, fill: '#16181d' }}>{v}%</text>
      </svg>
      {lbl && <div className="lbl">{lbl}</div>}
    </div>
  );
}

function Donut({ v }: { v: number }): JSX.Element {
  const r = 26, c = 2 * Math.PI * r, off = c * (1 - v / 100);
  const col = v >= 65 ? '#2f7d52' : v >= 50 ? '#b07a16' : '#bf4631';
  return (
    <svg width="74" height="74" viewBox="0 0 74 74" role="img" aria-label={`${v}%`}>
      <circle cx="37" cy="37" r={r} fill="none" stroke="#f0ede6" strokeWidth="9" />
      <circle cx="37" cy="37" r={r} fill="none" stroke={col} strokeWidth="9" strokeLinecap="round"
        strokeDasharray={c.toFixed(1)} strokeDashoffset={off.toFixed(1)} transform="rotate(-90 37 37)" />
      <text x="37" y="42" textAnchor="middle" style={{ fontWeight: 700, fontSize: 16, fill: '#16181d' }}>{v}%</text>
    </svg>
  );
}

function Card({ c, onNav, onCard, onProject, pinned, onPin }: {
  c: CardDef; onNav: (m: ModuleKey) => void; onCard: (c: CardDef) => void; onProject: (k: string) => void;
  pinned?: boolean; onPin?: () => void;
}): JSX.Element {
  const pinBtn = onPin ? (
    <button className={`kd-pin ${pinned ? 'on' : ''}`} aria-label={`${pinned ? 'Unpin' : 'Pin'} ${c.name}`} aria-pressed={pinned}
      onClick={(e) => { e.stopPropagation(); onPin(); }}>📌</button>
  ) : null;
  const body = (
    <>
      <div className="kd-ch">
        <div className="kd-ci">{c.icon}</div>
        <div className="kd-cn">{c.name}</div>
        <span className={`kd-rag ${c.rag[0]}`}>{c.rag[1]}</span>
        {pinBtn}
      </div>
      {c.donut != null ? (
        <div className="kd-cbody"><Donut v={c.donut} /><div className="kd-meta"><RichText value={c.meta} /></div></div>
      ) : c.bars ? (
        <>
          <div className="kd-cbody"><div className="kd-bars">{c.bars.map((b, i) => (
            <div className="kd-bar" key={i}><span className="bn">{b[0]}</span>
              <span className="bt"><span className={`bf ${b[2]}`} style={{ width: `${b[1]}%` }} /></span>
              <span className="bv">{b[3]}</span></div>
          ))}</div></div>
          <div className="kd-meta" style={{ marginTop: 12 }}><RichText value={c.meta} /></div>
        </>
      ) : c.big ? (
        <div className="kd-cbody"><div className="kd-big">{c.big[0]}<span className="u"> {c.big[1]}</span></div>
          <div className="kd-meta"><RichText value={c.meta} /></div></div>
      ) : (
        <div className="kd-meta"><RichText value={c.meta} /></div>
      )}
    </>
  );
  if (c.projKey) {
    const pk = c.projKey;
    return (
      <div className="kd-card nav" {...clickable(() => onProject(pk))} aria-label={`Open ${c.name}`}>
        {body}<div className="kd-arrow">→</div>
      </div>
    );
  }
  if (c.nav) {
    const target = c.nav;
    return (
      <div className="kd-card nav" {...clickable(() => onNav(target))} aria-label={`Open ${c.name} module`}>
        {body}<div className="kd-arrow">→</div>
      </div>
    );
  }
  return <div className="kd-card click" {...clickable(() => onCard(c))} aria-label={`Open ${c.name}`}>{body}</div>;
}

/** A workflow row: name + an inline mini-timeline (where it stands) + % + the current step. */
function WorkflowList({ workflows, onOpen, renderPin }: { workflows: Workflow[]; onOpen: (w: Workflow) => void; renderPin?: (w: Workflow) => JSX.Element }): JSX.Element {
  return (
    <div className="kd-wf-list">
      {workflows.map((w, i) => {
        const total = w.steps.length;
        const done = w.steps.filter((s) => s[1] === 'done').length;
        const nowIdx = w.steps.findIndex((s) => s[1] === 'now');
        const curi = nowIdx >= 0 ? nowIdx : done;
        const danger = /due|block|escalate|not started/i.test(w.due);
        const goodd = w.due === 'done' || done === total;
        const badgeStyle = danger
          ? { background: 'var(--kd-red-wash)', color: 'var(--kd-red)' }
          : goodd
            ? { background: 'var(--kd-green-wash)', color: 'var(--kd-green)' }
            : { background: '#f0f0ef', color: 'var(--kd-ink3)' };
        return (
          <div className="kd-wf click" key={i} {...clickable(() => onOpen(w))} aria-label={`Workflow: ${w.name}`}>
            <div className="kd-wfn"><b>{w.name}</b><div className="ld">Step {Math.min(curi + 1, total)} of {total} · {w.steps[curi]?.[0] ?? 'Complete'}</div></div>
            <div className="kd-wftrack" aria-hidden="true">
              {w.steps.map((s, j) => (
                <div className={`kd-step mini ${s[1] === 'done' ? 'done' : ''} ${s[1] === 'now' ? 'now' : ''}`} key={j}>
                  <div className="kd-sg" /><div className="kd-sd" />
                </div>
              ))}
            </div>
            <div className="kd-wfstat">
              <span className="kd-wfpct">{Math.round((done / total) * 100)}%</span>
              <span className="kd-badge" style={badgeStyle}>{w.due}</span>
            </div>
            {renderPin?.(w)}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Ask Kathedral panel — and its COLLAPSE object, the first build under N75
 * (the Five-W Genesis · Novello holds the map · proven inside + outside).
 *
 *   WHY   — the CEO controls their canvas; reclaim width to focus on a module
 *           or a drill when not actively asking. Their workspace, their call.
 *   WHAT  — a collapse/expand control that tucks the panel to a slim reopen
 *           rail and restores it (conversation state preserved across both).
 *   HOW   — `open` is owned by Dashboard; the control calls `onToggle`, which
 *           flips the state AND tags `.kdash.ask-collapsed` so the grid responds.
 *   WHEN  — on the user's click; default OPEN; the choice persists per browser.
 *   WHERE — lives in the third grid column; its message is the `ask-collapsed`
 *           class → the workspace column widens. Collapsed ALWAYS leaves a
 *           reopen affordance — no dead end (N75).
 */
/**
 * The dashboard cockpit's OWN Ask panel speaks the real POST /v3/ai/ask
 * contract with portal_context:'dashboard' (backend: src/v3/server/routes/ai.ts).
 * The global AskKathedralOverlay returns null on this surface by design — the
 * cockpit integrates its own panel — so this is the only caller that sends the
 * 'dashboard' context. AI Advisory Bucket A Step 2.
 */
/** Build the request body — a pure, testable seam (the 400-on-schema-mismatch
 *  class is guarded here). Delegates to the shared SSOT builder so every Ask
 *  surface emits an identical body shape. */
export function dashboardAskBody(prompt: string): AskRequestBody {
  return buildAskBody({ portalContext: 'dashboard', prompt });
}

/** The cockpit Ask panel's transport — the shared, governed /v3/ai/ask path. */
const dashboardAsk = (prompt: string): Promise<string> =>
  askKathedral({ portalContext: 'dashboard', prompt });

export function AskPanel({ open, onToggle, forTestAskFn }: {
  open: boolean;
  onToggle: () => void;
  /** Test seam: override the network call (mirrors AskKathedralOverlay). */
  forTestAskFn?: (prompt: string) => Promise<string>;
}): JSX.Element {
  // 'f' = failure. NOT 'k': a 'k' bubble carries the "K Kathedral" attribution
  // header, which signed our name on messages the model never produced.
  const [msgs, setMsgs] = useState<Array<{ role: 'u' | 'k' | 'f'; text: string }>>([]);
  const [input, setInput] = useState('');
  const [pending, setPending] = useState(false);
  const ask = (q: string): void => {
    setMsgs((prev) => [...prev, { role: 'u', text: q }]);
    setPending(true);
    (forTestAskFn ?? dashboardAsk)(q)
      .then((reply) => setMsgs((prev) => [...prev, { role: 'k', text: reply }]))
      .catch((err: unknown) =>
        setMsgs((prev) => [
          ...prev,
          { role: 'f', text: classifyAskFailure(err).userMessage },
        ]),
      )
      .finally(() => setPending(false));
  };
  const send = (): void => { const q = input.trim(); if (q && !pending) { ask(q); setInput(''); } };
  // Collapsed: a slim, always-present reopen rail (never a dead end). The panel
  // stays mounted, so the conversation survives a collapse/reopen.
  if (!open) {
    return (
      <aside className="kd-ask collapsed" aria-label="Ask Kathedral">
        <button className="kd-askreopen" onClick={onToggle} aria-label="Open Ask Kathedral" aria-expanded={false}>
          <span className="kd-kb">K</span><span className="vtxt">Ask Kathedral</span>
        </button>
      </aside>
    );
  }
  return (
    <aside className="kd-ask" aria-label="Ask Kathedral">
      {/* The ENTIRE header toggles the panel (E3 2026-07-03) — not just the
          chevron. One button; the › is a visual affordance (aria-hidden). */}
      <button type="button" className="kd-ah" onClick={onToggle} aria-label="Collapse Ask Kathedral" aria-expanded={true}>
        <div className="kd-who"><span className="kd-kb">K</span><span className="nm">Ask Kathedral</span>
          <span className="kd-askcollapse" aria-hidden="true">›</span></div>
        <div className="kd-asub">I assembled this view from every stream this morning. Ask me anything.</div>
      </button>
      <div className="kd-abody">
        {msgs.length === 0 ? (
          <>
            <p className="kd-lead">Suggested</p>
            {SUGGESTED.map((q, i) => (
              <button className="kd-q" key={i} onClick={() => ask(q)}>{q}<span className="qa">→</span></button>
            ))}
          </>
        ) : (
          // Assistant content is model output · render as text (never HTML) per
          // the input-boundary discipline (no dangerouslySetInnerHTML on replies).
          msgs.map((mm, i) => mm.role === 'u'
            ? <div className="kd-bubble u" key={i}><div className="bx">{mm.text}</div></div>
            : mm.role === 'f'
              // No "K Kathedral" header: an unanswered request is not attributed
              // to Kathedral, and role="alert" announces it instead of letting it
              // read as the next reply in the thread.
              ? <div className="kd-bubble f" key={i} role="alert"><div className="bx"><strong>Not answered — </strong>{mm.text}</div></div>
              : <div className="kd-bubble k" key={i}><div className="kh"><span className="kd-kk">K</span> Kathedral</div><div className="bx">{mm.text}</div></div>)
        )}
        {pending && (
          <div className="kd-bubble k" aria-live="polite"><div className="bx">Thinking…</div></div>
        )}
      </div>
      <div className="kd-composer">
        <input value={input} onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
          disabled={pending}
          placeholder="Ask Kathedral, or describe a view…" aria-label="Ask Kathedral" />
        <button onClick={send} aria-label="Send" disabled={pending}>→</button>
      </div>
    </aside>
  );
}

/** A built-out domain-layer tab: a panel of rows; each row opens a drill (no dead ends). */
function TabPanelView({ panel, onRow, renderPin }: { panel: TabPanel; onRow: (r: TabRow) => void; renderPin?: (r: TabRow) => JSX.Element }): JSX.Element {
  return (
    <div className="kd-panel">
      {panel.lead && <div className="kd-panel-lead">{panel.lead}</div>}
      <div className="kd-rows">
        {panel.rows.map((r, i) => (
          <div className="kd-row click" key={i} {...clickable(() => onRow(r))} aria-label={r.title}>
            <div className="kd-rowm">
              <div className="kd-rowt">{r.title}</div>
              {r.meta && <div className="kd-rowsub"><RichText value={r.meta} /></div>}
            </div>
            {r.src && <span className={`kd-src ${r.src}`}>{r.src === 'yardi' ? 'Yardi' : r.src === 'kathedral' ? 'Kathedral' : 'Manual'}</span>}
            {r.rag && <span className={`kd-rag ${r.rag[0]}`}>{r.rag[1]}</span>}
            {r.right && <span className="kd-rowright">{r.right}</span>}
            {renderPin?.(r)}
          </div>
        ))}
      </div>
    </div>
  );
}

export function Dashboard({ scrollContainerRef }: { scrollContainerRef?: RefObject<HTMLElement | null> } = {}): JSX.Element {
  const [moduleKey, setModuleKey] = useState<ModuleKey>('executive');
  const [tab, setTab] = useState('Overview');
  // Drill STACK (Theme 7 · bug_UI_1d) — opening a drill from inside a drill
  // overlays it; closing pops back to the parent, then the app. Never lose your
  // place. Context travels on each drill (def.ctx), so Prev/Next + stepper clicks
  // work at any depth without separate state.
  const [stack, setStack] = useState<DrillDef[]>([]);
  const drill = stack.length ? stack[stack.length - 1] : null;
  const openDrill = (d: DrillDef): void => setStack([d]);              // fresh top-level drill
  const pushDrill = (d: DrillDef): void => setStack((s) => [...s, d]); // overlay a nested drill
  const replaceDrill = (d: DrillDef): void => setStack((s) => [...s.slice(0, -1), d]); // walk in place
  const closeDrill = (): void => setStack((s) => s.slice(0, -1));      // pop one (reveal parent)
  const clearDrills = (): void => setStack([]);
  // ── Developer mode + the template being edited (E3 2026-08-29) ─────────
  // Gated on the scope that already draws the dev-side wall. A PHA seat never
  // holds it, so the edit affordances are ABSENT rather than disabled — a
  // control a user can see but not use is an invitation to ask why.
  const { scopes, organizations, activeOrgId } = useAuth();
  const { deals: DEALS } = useDeals();
  const developer = isDeveloper(scopes);
  // Template state lives in a STORE, not here: the Portal dropdown in the shell
  // acts on the same template, and two component trees needing one piece of
  // state is what a store is for.
  const { template, editing, dirty, edit, setEditing, save, saveAs, hydrate } =
    useTemplateStore();

  /**
   * Read the template from the server, once, after the dashboard mounts.
   *
   * This line exists because a template is now DATA (migrations 0173/0174) and a
   * server read is asynchronous, while the store is constructed synchronously.
   * The store initialises from cache-or-default so this never delays the first
   * frame — the server's answer replaces what is on screen when it arrives, and
   * `hydrate` is a no-op once hydrated or when there are unsaved edits.
   *
   * It runs HERE rather than at module load because the read requires a session:
   * firing it before authentication would only produce a 401.
   */
  useEffect(() => hydrate(), [hydrate]);

  /**
   * Turning editing OFF is what saves — E3: "when I click on it again, that
   * essentially saves the template in its current state." One control, no
   * separate confirm to forget, and no way to leave edit mode with work hanging.
   */
  const toggleEditing = (): void => {
    if (editing) save();
    setEditing(!editing);
  };

  const [proj, setProj] = useState<string | null>(null);
  // Shape from the catalog, content from a supplier — and the catalog is typed
  // `ModuleShape`, which has no content fields at all, so nothing static can
  // leak in even by mistake. (This used to call `bareModule()` to strip them;
  // the type makes that unnecessary.)
  const content = useDashboardContent();
  const supplied = content?.modules?.[moduleKey];
  /**
   * Bare = render SHAPE, not values.
   *
   * ── Two questions, one flag (E3 lock 2026-09-08) ──────────────────────────
   * E3: *"Templates are blank slates. They are lenses into the data, which
   * therefore becomes a dashboard, which there is an aggregate of modules.
   * There is no data within the template."*
   *
   * This used to ask only *"did a supplier show up?"*. That left template-edit
   * mode rendering live values: E3 opened production Everett with `Save
   * template` and the element palette on screen, and `ACTIVE PROJECTS 1` /
   * `DEVELOPMENT 1` underneath them. A blank slate rendering real data.
   *
   * So it now also asks *"am I editing the shape?"*. `editing` was already in
   * scope and the render simply never consulted it.
   *
   * ── What this does NOT touch ──────────────────────────────────────────────
   * The edit affordances — palette, drag handles, the tab strip's `editing`
   * class — key off `editing` directly, not off `bare`. The editor stays fully
   * usable; only the DATA goes away. And nothing that renders outside edit mode
   * stops rendering: this is a widening, not a rewrite.
   *
   * ── The known cost, named by E3 rather than hidden ────────────────────────
   * *"a developer editing it cannot see how it reads with real data… we should
   * probably think of ways to mitigate this."* True, and a separate mitigation
   * — not a reason to weaken the blank slate. Once the template is a dashboard
   * in a CEO's hands, it customizes WITH data, which is where that need is met.
   */
  const bare = editing || supplied === undefined;
  /**
   * ⚠️ The spread MUST be guarded on `bare`, not left to `supplied` being
   * undefined.
   *
   * This line used to read `...supplied` unconditionally, and was correct only
   * because `bare` MEANT `supplied === undefined` — so whenever bare was true,
   * the spread was a no-op anyway. Widening `bare` to include `editing` broke
   * that coincidence: a supplier now exists while bare is true, and the values
   * came straight back through here even though every downstream `bare` check
   * was doing its job.
   *
   * Caught by the blank-slate test on its first run, which is exactly the class
   * of thing a test that supplies data and then asserts its ABSENCE exists for.
   */
  const base: ModuleDef = {
    ...MODULES[moduleKey],
    ...EMPTY_MODULE_CONTENT,
    ...(bare ? {} : supplied),
  };

  /**
   * The agency's own name, from its own session.
   *
   * The Executive hub is titled with WHOSE agency this is, and that is data —
   * it used to be the constant 'Riverdale Housing Authority', which every
   * island rendered regardless of who was logged in. `useAuth()` already
   * carries the real name for the active org, so the honest source was one
   * field away the whole time.
   *
   * A supplier's title still wins: if content arrives carrying one, it is the
   * more specific answer and this never overrides it.
   */
  const agencyName = organizations.find((o) => o.id === activeOrgId)?.name ?? '';
  /**
   * The heading, in order of specificity: what a supplier said, then (for the
   * hub) whose agency this is, then the module's own name.
   *
   * `label` is the floor because it is SHAPE — 'Board', 'Planning' — true for
   * every customer and never blank. A heading that disappears when nothing is
   * bound is correct data-wise and useless to the person reading it (N84).
   *
   * ── While EDITING, the heading is shape too (E3 lock 2026-09-08) ──────────
   * `agencyName` comes from the session, not from the supplier, so `bare` alone
   * does not reach it — the Executive hub would still have read "Evan (Dev
   * Admin)'s Workspace" in template-edit mode, which is data by E3's own
   * definition ("there is no data within the template").
   *
   * So editing falls through to `label`: the hub reads 'Executive'. This is the
   * LITERAL reading of the lock, and the conservative choice when the principle
   * itself is what is under test.
   *
   * The alternative reading — that an agency's name is the IDENTITY of the
   * surface being edited rather than content within it, closer to a window
   * title than to a value — is defensible and is E3's to pick. It is recorded
   * here and pinned by a named test rather than silently chosen
   * ([[feedback_never_unilaterally_choose_a_behavior_2026_06_20]]); reversing it
   * is one condition.
   *
   * Either way a heading always renders, so N84 holds in both readings.
   */
  const m: ModuleDef =
    base.title !== ''
      ? base
      : {
          ...base,
          title:
            moduleKey === 'executive' && agencyName !== '' && !editing ? agencyName : base.label,
        };

  /**
   * The tab panel a supplier provided, or the catalog's (which is empty).
   *
   * ⚠️ `bare` gates BOTH operands, not just the fallback.
   *
   * This read `content?.tabPanels?.[…] ?? (bare ? undefined : TAB_PANELS[…])`,
   * which guards the catalog default and lets the SUPPLIER straight through —
   * so a template being edited rendered the agency's real panel content on
   * every tab that had any. Same shape, same line-pair, as the `...supplied`
   * spread above: the guard was placed on the branch that was already empty.
   *
   * Enumerated over all 22 catalog tabs by
   * `test/dashboard/every-tab-is-a-blank-slate.test.tsx`, which failed on all
   * 22 before this change.
   */
  const suppliedPanel: TabPanel | undefined = bare
    ? undefined
    : (content?.tabPanels?.[moduleKey]?.[tab] ?? TAB_PANELS[moduleKey][tab]);

  /** The workflows for this module — one expression, used twice below. */
  const tabWorkflows: Workflow[] = bare
    ? []
    : (content?.workflows?.[moduleKey] ?? WORKFLOWS[moduleKey]);

  /**
   * Does THIS TAB have anything to show?
   *
   * ── The defect this replaces (E3, production Everett, 2026-09-08) ──────────
   * Every tab description vanished the moment a supplier existed. The render
   * gated `TabSummary` on `bare` — a WHOLE-DASHBOARD flag (`content.modules[key]
   * === undefined`) standing in for a PER-TAB question. `DashboardHome` began
   * mounting `DashboardContentProvider` in PR #366, and that supplier fills
   * `kpis` and nothing else — so `bare` went false everywhere while four tabs
   * out of five still had nothing in them. Workflow rendered an empty list with
   * no text at all; the rest fell through to a one-line note.
   *
   * A partial supplier is the normal state and will be for every wave between
   * here and a full one, so "is anything supplied anywhere" can never answer
   * "should this tab explain itself". This asks the question the render actually
   * needs.
   */
  const tabHasContent: boolean =
    tab === 'Overview'
      ? m.apex.html !== '' ||
        m.kpis.length > 0 ||
        (m.needs?.length ?? 0) > 0 ||
        m.cards.length > 0
      : tab === 'Workflow'
        ? tabWorkflows.length > 0
        : suppliedPanel !== undefined;

  /**
   * "Tell me more" — the purpose stays reachable once data arrives.
   *
   * E3 2026-09-08: *"restore all of the tab descriptions, although they might
   * not be visible when we populate that data. I do want some sort of
   * description either on a hover over or a right click on tell me more."*
   *
   * So the description is INLINE while the tab is empty, and behind this toggle
   * once it is not — the same text either way, never a second copy that can
   * drift from the first.
   */
  const [purposeOpen, setPurposeOpen] = useState(false);
  // Collapse it on tab change: a panel left open from another tab would be
  // describing something the reader is no longer looking at.
  useEffect(() => setPurposeOpen(false), [tab, moduleKey]);

  /**
   * The whole edit vocabulary, in one function — E3's Apple model.
   *
   * From the package onto the module = add. Off the module = remove, whether it
   * was dropped back on the package or simply let go somewhere else (Finder
   * treats both as removal, and so does this). Anything else is a drag the user
   * abandoned, and abandoning must be free.
   */
  /**
   * The whole edit vocabulary, in one function — E3's Apple model.
   *
   * Three gestures and no buttons. From the palette onto the page = add. Off the
   * module = remove, whether dropped back on the palette or simply let go
   * (Finder treats both as removal, and so does this). A module onto another
   * module's slot = reorder; a module let go anywhere else = remove it from the
   * workspace.
   *
   * Permanent modules never reach here — they carry no drag handle at all — and
   * the state layer refuses them regardless, so the rule holds even if a future
   * caller forgets.
   */
  const onTemplateDrop = (payload: DragPayload, target: DropTarget): void => {
    if (payload.from === 'module') {
      if (target.kind === 'module-slot' && target.moduleKey !== payload.moduleKey) {
        edit((t) => {
          const order = t.modules.map((mod) => mod.key);
          const from = order.indexOf(payload.moduleKey as ModuleKey);
          const to = order.indexOf(target.moduleKey as ModuleKey);
          if (from === -1 || to === -1) return t;
          return moveModule(t, payload.moduleKey as ModuleKey, to > from ? 1 : -1);
        });
        return;
      }
      if (target.kind !== 'module-slot') {
        edit((t) => hideModule(t, payload.moduleKey as ModuleKey));
      }
      return;
    }
    if (payload.from === 'package' && target.kind !== 'package') {
      edit((t) => placeElement(t, moduleKey, payload.kind, elementsOf(t, moduleKey).length));
      return;
    }
    if (payload.from === 'placed') {
      edit((t) => unplaceElement(t, moduleKey, payload.id));
    }
  };

  // Back-stack: sync the overlay layers (project view + drill stack) to browser
  // history so the OS back gesture pops ONE layer instead of leaving the app —
  // kills the UI_BUG_1a mobile dead-end class (Fable mobile strategy §3b · §6 A).
  // Pop is top-first: a drill before the project underneath it.
  const layerDepth = (proj ? 1 : 0) + stack.length;
  const popLayer = (): void => {
    if (stack.length) closeDrill();
    else if (proj) setProj(null);
  };
  useLayerHistory(layerDepth, popLayer);

  // Scroll discipline: on any lateral navigation (module / project / tab change)
  // reset the ACTIVE scroll container to top so content never changes under the
  // user's thumb mid-scroll (Fable §3a mechanism 4 · §6 B). Mobile scrolls the
  // shell wrapper (scrollContainerRef); desktop scrolls the internal .kd-work.
  const workRef = useRef<HTMLElement>(null);
  useEffect(() => {
    scrollContainerRef?.current?.scrollTo?.({ top: 0 });
    workRef.current?.scrollTo?.({ top: 0 });
  }, [moduleKey, proj, tab, scrollContainerRef]);

  // Pins persist per browser (localStorage now; per-user Preferences with Part 9).
  //
  // VALIDATED, not cast. `JSON.parse(...) as Pin[]` was a lie the compiler could
  // not catch: the try/catch handles a PARSE failure and says nothing about
  // SHAPE. Stored `{}` is valid JSON, so the cast passed and `pins.map(...)`
  // threw on a non-array — a white screen from a value written by an older
  // build, which is the exact mechanism that killed the dashboard twice on
  // 2026-08-29 via the template store.
  //
  // A persisted shape is a published contract. Anything unrecognisable is
  // dropped rather than trusted, because the input was written by code we can
  // no longer negotiate with, and a lost pin is a smaller harm than a dead page.
  const [pins, setPins] = useState<Pin[]>(() => loadPins());
  useEffect(() => {
    try { localStorage.setItem('kd-pins', JSON.stringify(pins)); } catch { /* non-persistent env */ }
  }, [pins]);
  const [qaOpen, setQaOpen] = useState(false);
  // Rail expand/collapse — pin the module NAMES open (per 2f), persisted per browser.
  const [railOpen, setRailOpen] = useState<boolean>(() => {
    try { return localStorage.getItem('kd-rail-open') === '1'; } catch { return false; }
  });
  const toggleRail = (): void => setRailOpen((o) => {
    const n = !o;
    try { localStorage.setItem('kd-rail-open', n ? '1' : '0'); } catch { /* non-persistent env */ }
    return n;
  });
  // Ask Kathedral collapse (N75 first build) — default OPEN on desktop, persisted
  // per browser. On phones (≤768px) it defaults COLLAPSED so its control is a
  // reachable bottom bar rather than a panel buried at the end of the scroll —
  // same collapse/expand model as the web side, control adapted for mobile.
  const [askOpen, setAskOpen] = useState<boolean>(() => {
    try {
      if (typeof window !== 'undefined' && window.innerWidth <= 768) return false;
      return localStorage.getItem('kd-ask-open') !== '0';
    } catch { return true; }
  });
  const toggleAsk = (): void => setAskOpen((o) => {
    const n = !o;
    try { localStorage.setItem('kd-ask-open', n ? '1' : '0'); } catch { /* non-persistent env */ }
    return n;
  });

  const go = (k: ModuleKey): void => { setModuleKey(k); setTab('Overview'); clearDrills(); setProj(null); };
  const goTab = (t: string): void => { setTab(t); clearDrills(); setProj(null); };
  // Switch INTO the projects module too, so opening a project from a drill in any
  // other module actually lands on the project view (bug_UI_1j "take me there").
  const openProject = (k: string): void => { setModuleKey('projects'); setProj(k); clearDrills(); };
  // Open a milestone drill (top-level, from the lifecycle). Its ctx rides on the
  // def, so Prev/Next (walk in place) + stepper clicks (overlay) work from it.
  const openMilestone = (deal: string, i: number): void => openDrill(milestoneDrill(DEALS[deal], PHASES, i));
  const isPinned = (id: string): boolean => pins.some((p) => p.id === id);
  const togglePin = (pin: Pin): void => setPins((prev) => prev.some((p) => p.id === pin.id) ? prev.filter((p) => p.id !== pin.id) : [...prev, pin]);
  const openPin = (p: Pin): void => {
    setQaOpen(false);
    if (p.kind === 'project') { openProject(p.key); return; }
    go(p.key as ModuleKey);           // go() lands on Overview + clears drills
    if (p.tab && p.tab !== 'Overview') setTab(p.tab); // an item pinned from another tab
  };
  // A pin button for any content OBJECT (KPI tile, need, apex, workflow/tab row).
  // Clicking its rail shortcut returns to the object's module (+ tab); un-pin from
  // the object itself. stopPropagation so it doesn't trigger the tile's own click.
  const itemPin = (id: string, label: string, tab?: string): JSX.Element => (
    <button
      className={`kd-pin kd-pin-tile ${isPinned(id) ? 'on' : ''}`}
      aria-label={`${isPinned(id) ? 'Unpin' : 'Pin'} ${label}`}
      aria-pressed={isPinned(id)}
      title={`${isPinned(id) ? 'Unpin' : 'Pin'} ${label}`}
      onClick={(e) => { e.stopPropagation(); togglePin({ id, label, kind: 'item', key: moduleKey, tab }); }}
    >📌</button>
  );

  return (
    // ONE drag context for the whole editing surface. It used to wrap only the
    // module body, which left the rail outside it — so a module could never be
    // dragged and the arrows had to stay. Hoisting it is what lets the rail obey
    // the same grammar as everything else: drag to reorder, drag out to remove.
    <TemplateDndProvider onDrop={onTemplateDrop}>
    <div className={`kdash ${railOpen ? 'rail-open' : ''} ${askOpen ? '' : 'ask-collapsed'}`}>
      {/* rail */}
      <nav className="kd-rail" aria-label="Modules">
        {/* The rail's "K" mark was removed 2026-08-29 (E3: "we already have
            Kathedral.ai with the house logo above that"). Two brand marks
            stacked within a few pixels of each other is repetition, not
            reinforcement — the wordmark in the top bar already carries it. */}
        <button className="kd-railtoggle" onClick={toggleRail} aria-expanded={railOpen}
          aria-label={railOpen ? 'Collapse module rail' : 'Expand module rail'}>
          <span className="kd-ic" aria-hidden="true">{railOpen ? '«' : '»'}</span>
        </button>
        {/* The rail renders the TEMPLATE's modules in the TEMPLATE's order — not
            the shipped constant — which is what makes reordering and removal
            real rather than cosmetic. */}
        {visibleModules(template).map((tm) => {
          const k: ModuleKey = tm.key;
          const md = MODULES[k];
          const navButton = (
            <button className={`kd-nav ${k === moduleKey ? 'on' : ''}`} onClick={() => go(k)}
              aria-current={k === moduleKey ? 'page' : undefined}>
              <span className="kd-ic" aria-hidden="true">{md.icon}</span>
              <span className="kd-lb">{md.label}</span>
            </button>
          );
          return (
            <div className={`kd-navrow ${editing ? 'editing' : ''}`} key={k}>
              {/* No arrows, no ×. E3: "Instead of arrows up and down, we just do
                  the drag and drop." Drag to reorder, drag out to remove — and a
                  PERMANENT module (Executive · Projects) carries no handle at
                  all, because a control that cannot act is worse than none. */}
              {editing ? (
                <DraggableModule moduleKey={k} permanent={isPermanentModule(k)} label={md.label}>
                  {navButton}
                </DraggableModule>
              ) : (
                navButton
              )}
              {/* The rail drill-down was removed 2026-08-29. E3: "since we have
                  it as a tab, let's remove import data from the navigation bar.
                  That's not gonna work, and it just looks kinda strange."
                  A rail entry that only repeats a tab is a second name for one
                  destination — two places to maintain and one more thing to
                  read past. The tab is enough. */}
            </div>
          );
        })}
        {/* Pinned items live in the rail (bug_UI_1i) — instant return to any pinned
            module / project / card. Also mirrored in the QuickAccess dropdown. */}
        {pins.length > 0 && (
          <div className="kd-railpins" aria-label="Pinned">
            <div className="kd-railpinh" aria-hidden="true">Pinned</div>
            {pins.map((p) => {
              // Make the shortcut EXPLICIT about where it leads — the pin names
              // the object (label) and its sub-line reads "→ <destination>", the
              // exact place a click opens:
              //   • a CARD    → opens its home module (e.g. "→ Board")
              //   • a PROJECT → opens in the Projects module ("→ Projects")
              //   • a MODULE  → opens its own view, named by the eyebrow
              //                 descriptor (Planning → "→ The Strategy")
              const mod = MODULES[p.key as ModuleKey];
              const dest =
                p.kind === 'card' || p.kind === 'item'
                  ? mod?.label ?? 'its module'
                  : p.kind === 'project'
                    ? 'Projects'
                    : mod?.eyebrow.split('·')[1]?.trim() || mod?.label || 'its view';
              return (
                <button
                  key={p.id}
                  className="kd-nav kd-railpin"
                  onClick={() => openPin(p)}
                  title={`Open ${p.label} → ${dest}`}
                >
                  <span className="kd-ic" aria-hidden="true">{p.kind === 'project' ? '⌂' : p.kind === 'module' ? '◆' : '📌'}</span>
                  <span className="kd-lb">
                    <span className="kd-railpin-main">{p.label}</span>
                    <span className="kd-railpin-sub">→ {dest}</span>
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </nav>

      {/* workspace */}
      <main className="kd-work" ref={workRef}>
        <div className="kd-top">
          <div className="kd-crumb">{m.label} <span style={{ color: 'var(--kd-line2)' }}>›</span> <b>{proj && moduleKey === 'projects' ? DEALS[proj].name : m.title}</b></div>
          <div className="kd-topright">
            <div className="kd-qa">
              <button className="kd-qabtn" onClick={() => setQaOpen((o) => !o)} aria-expanded={qaOpen} aria-label={`Quick Access — ${pins.length} pinned`}>📌 {pins.length}</button>
              {qaOpen && (
                <div className="kd-qamenu" role="menu" aria-label="Quick Access">
                  <div className="kd-qahead">Quick Access</div>
                  {pins.length === 0
                    ? <div className="kd-qaempty">Pin a module or project for instant return.</div>
                    : pins.map((p) => (
                      <button className="kd-qaitem" role="menuitem" key={p.id} onClick={() => openPin(p)}>
                        <span aria-hidden="true">{p.kind === 'project' ? '⌂' : '◆'}</span> {p.label}
                      </button>
                    ))}
                </div>
              )}
            </div>
            {/* The account identity lives in the global TopBar chip (UserMenu);
                a second hardcoded avatar here was redundant + wrong-identity
                ("move the EC" · bug_UI_1b 2026-07-02). Removed. */}
          </div>
        </div>
        {moduleKey === 'projects' && proj ? (
          <ProjectView dealKey={proj} onBack={() => setProj(null)} onOpen={openProject} onTick={(mtr, v) => openDrill(tickDrill(mtr, v))}
            onMilestone={(i) => openMilestone(proj, i)}
            pinned={isPinned(`project:${proj}`)}
            onTogglePin={(label) => togglePin({ id: `project:${proj}`, label, kind: 'project', key: proj })}
            isCardPinned={(id) => isPinned(id)}
            onToggleCardPin={(id, label) => togglePin({ id, label, kind: 'project', key: proj })} />
        ) : (
          <>
        <div className="kd-head">
          {/* ── The dashboard leads with the DASHBOARD (E3 2026-09-30) ──────
              E3: *"When any user logs in, the first thing is not to be seen is
              the edit template. The first thing a user, any user, should see is
              the actual dashboard... let's remove the edit template button
              because that is an administrator level only."*

              So the bar renders only while editing is ALREADY ON. Turning it on
              is an administrator action taken from the Portal dropdown's
              Template menu ("Edit this template"), which is scope-gated and is
              where creating and editing templates belongs.

              Once editing is on the bar must be here — it carries the save and
              the way back out, and an edit mode with no exit is a trap. Still
              ABSENT rather than disabled for anyone who cannot author: a control
              a user can see but not use is an invitation to ask why. */}
          {developer && editing && (
            <EditBar
              editing={editing}
              onToggle={toggleEditing}
              templateName={template.name}
              onSaveAs={saveAs}
              dirty={dirty}
            />
          )}
          {/* No remove button. Drag it out — E3: "There are no x's and arrows by
              the existing elements. There's just the importing and exporting."
              The wrapper adds a drag handle around the element and changes
              nothing about its own markup, so the layout is untouched. */}
          {elementVisible(template, moduleKey, 'eyebrow') &&
            (editing ? (
              <DraggablePlaced kind="eyebrow" id="eyebrow" permanent={isPermanentElement('eyebrow')} label="the eyebrow">
                <div className="kd-eyebrow">{m.eyebrow}</div>
              </DraggablePlaced>
            ) : (
              <div className="kd-eyebrow">{m.eyebrow}</div>
            ))}
          {/* The title carries NO control in either mode: it is the module's
              identity and cannot be removed (E3 — "we will never want to get rid
              of the titles and namings"). The state layer refuses it too, so
              this is a rule rather than an omission. */}
          {elementVisible(template, moduleKey, 'title') && <h1 className="kd-h1">{m.title}</h1>}
          {/* The module itself is a pinnable object — its pin lives right on its
             header, so a module-pin shortcut can be un-pinned where it lands
             (same id as the shortcut, so state stays in lockstep). */}
          <button
            className={`kd-pin kd-pin-head ${isPinned(`module:${moduleKey}`) ? 'on' : ''}`}
            aria-label={`${isPinned(`module:${moduleKey}`) ? 'Unpin' : 'Pin'} ${m.label}`}
            aria-pressed={isPinned(`module:${moduleKey}`)}
            title={`${isPinned(`module:${moduleKey}`) ? 'Unpin' : 'Pin'} ${m.label}`}
            onClick={() => togglePin({ id: `module:${moduleKey}`, label: m.label, kind: 'module', key: moduleKey })}
          >📌</button>
        </div>
        {elementVisible(template, moduleKey, 'tabs') && (
          <div className="kd-tabs" role="tablist" aria-label={`${m.label} domain layers`}>
            {/* Tabs come from the template too, so membership AND order are the
                developer's rather than the shipped definition's. */}
            {tabsOf(template, moduleKey).map((t) => (
              <span className={`kd-tabwrap ${editing ? 'editing' : ''}`} key={t}>
                <button className={`kd-tab ${t === tab ? 'on' : ''}`} role="tab"
                  aria-selected={t === tab} onClick={() => goTab(t)}>{t}</button>
                {/* The ↑/↓ arrows are gone: E3 — "the arrows next to the tabs
                    within the module make no sense because there's no up or down
                    in that." A tab strip is horizontal. Tab reordering lands
                    with the sortable pass, which needs the list-based render. */}
              </span>
            ))}
            {/* ── "Tell me more" (E3 2026-09-08) ───────────────────────────
                Once a tab HAS data, its description stops occupying the
                surface — but it must stay reachable, not be deleted. This is
                that door. It appears only when the description is not already
                inline, so the reader is never offered a control that reveals
                what is visible six pixels below it.

                A real <button>, not a hover-only affordance: `title` gives the
                pointer user the hover E3 asked for, and the button gives the
                keyboard and touch user the same content — one implementation,
                WCAG 2.2 AA, no hover-trap. */}
            {tabHasContent && tabPurpose(moduleKey, tab) !== null && (
              <button
                type="button"
                className={`kd-tab-more ${purposeOpen ? 'on' : ''}`}
                aria-expanded={purposeOpen}
                aria-controls={`kd-purpose-${moduleKey}-${tab}`}
                title={`What is the ${tab} tab for?`}
                aria-label={
                  purposeOpen
                    ? `Hide what the ${tab} tab is for`
                    : `Tell me more about the ${tab} tab`
                }
                onClick={() => setPurposeOpen((o) => !o)}
              >
                ⓘ
              </button>
            )}
            {/* The strip itself drags out as one element, like any other. */}
          </div>
        )}

        {/* The purpose, revealed on demand over a populated tab. Same component
            as the inline one below — one source of the text, never two. */}
        {tabHasContent && purposeOpen && (
          <div id={`kd-purpose-${moduleKey}-${tab}`} className="kd-purpose-reveal">
            <TabSummary moduleKey={moduleKey} tab={tab} label={m.label} />
          </div>
        )}

        {/* Everything removed, offered back. The tray is what makes removal safe
            to try: nothing leaves the template, it is set aside. */}
        {/* ── The package (E3 2026-08-29) ─────────────────────────────────
            Modelled on macOS "Customize Toolbar…": a complete, static tray of
            every element a module can hold. Drag one in to add it, drag one out
            to remove it. Nothing is destroyed — removing returns it here, which
            is why the tray can be the only place you ever need to look. */}
        {editing && (
          <PackageDropZone>
            <TemplatePackage
              isPlaced={(kind) => elementVisible(template, moduleKey, kind)}
              onAdd={(kind) => edit((t) => showElement(t, moduleKey, kind))}
              onApplyDefaults={() => edit(() => freshTemplate(template.name))}
            />
          </PackageDropZone>
        )}
        {editing && (
          <RemovedTray
            template={template}
            moduleKey={moduleKey}
            onRestoreModule={(k) => edit((t) => showModule(t, k))}
            onRestoreTab={(t0) => edit((t) => showTab(t, moduleKey, t0))}
          />
        )}

        {/* With no supplier, every tab states its own purpose instead of showing
            a figure it cannot source. Supply content and the data path below
            returns untouched — same components, same markup. */}
        {/* Import Data is the one tab with real functionality behind it, and it
            lives HERE rather than on a page of its own — E3's rule: the customer
            is never taken away from the workflow to add something to it. */}
        {moduleKey === 'projects' && tab === 'Import Data' ? (
          /* `editing`, NOT `bare`. This tab's rows come from the API rather than
             the content supplier, so the "did a supplier show up?" half of `bare`
             does not apply to it — gating on `bare` would kill import for a real
             customer whenever no supplier was bound.

             E3 2026-09-09, screenshot of production Everett: *"the template
             cannot have Dev_1a in the template. That is a bug."* This element
             rendered with no props at all, so the component could not tell a
             developer authoring the shape from a customer opening the tab
             unbound, and fetched `/v3/projects` for both. The blank slate was
             never wrong here — it was never ASKED. */
          <ImportDataTab editingTemplate={editing} />
        ) : !tabHasContent ? (
          elementVisible(template, moduleKey, 'summary') ? (
            <div className={editing ? 'kd-editable' : undefined}>
              {/* The last × is gone. Drag it out like everything else. */}
              {editing ? (
                <DraggablePlaced kind="summary" id="summary" permanent={isPermanentElement('summary')} label="the tab summary">
                  <TabSummary moduleKey={moduleKey} tab={tab} label={m.label} />
                </DraggablePlaced>
              ) : (
                <TabSummary moduleKey={moduleKey} tab={tab} label={m.label} />
              )}
            </div>
          ) : null
        ) : tab === 'Overview' ? (
          <div>
            {m.apex.html !== '' && (
            <div className="kd-apex">
              <div className="say">
                <p><RichText value={m.apex.html} /></p>
                <span className="kd-verify">⛓ every figure traces to source · verify me</span>
              </div>
              {m.apex.ring != null && <Ring v={m.apex.ring} lbl={m.apex.ringLbl} />}
              {itemPin(`apex:${moduleKey}`, `${m.label} — overview`)}
            </div>
            )}

            <div className="kd-kpis">
              {m.kpis.map((k, i) => (
                <div className="kd-kpi click" key={i} {...clickable(() => openDrill(kpiDrill(k)))} aria-label={`${k.k}: ${k.v}`}>
                  <div className="k">{k.k}</div>
                  <div className="v">{k.v}</div>
                  <div className="s"><RichText value={k.s} /></div>
                  {itemPin(`kpi:${moduleKey}:${k.k}`, k.k)}
                </div>
              ))}
            </div>

            {m.needs && (
              <>
                <div className="kd-sec"><h2>What needs you</h2><span className="ct">{m.needs.length}</span>
                  <span className="hint">ranked by urgency</span></div>
                <div className="kd-needs">
                  {m.needs.map((n, i) => (
                    <div className={`kd-need click ${n.tone}`} key={i} {...clickable(() => openDrill(needDrill(n)))} aria-label={n.t}>
                      <div className="pr">{n.p}</div>
                      <div className="m">
                        <div className="t">{n.t}</div>
                        <div className="d"><RichText value={n.d} /></div>
                        <div className="ai"><span className="kd-kk">K</span> {n.ai}</div>
                      </div>
                      {itemPin(`need:${moduleKey}:${i}`, n.t)}
                    </div>
                  ))}
                </div>
              </>
            )}

            {m.cards.length > 0 && <div className="kd-sec"><h2>{m.sec}</h2><span className="hint">{m.secHint}</span></div>}
            {/* `ProjectGrid` reads `DEALS` / `DEAL_ORDER` from `project-data.ts`
                DIRECTLY — it never passes through `m.cards`, so emptying the
                module data left its ten cards on screen. Brought under the same
                flag: the bare template shows no projects anywhere. */}
            {moduleKey === 'projects' ? (
              bare ? null : (
              <ProjectGrid onOpen={openProject}
                isPinned={(k) => isPinned(`project:${k}`)}
                onTogglePin={(k, label) => togglePin({ id: `project:${k}`, label, kind: 'project', key: k })} />
              )
            ) : (
            <div className={`kd-grid ${m.cards[0]?.type !== 'stream' ? 'p3' : ''}`}>
              {m.cards.map((c, i) => {
                // Every card is pinnable (bug_UI_1i): project → its view, nav →
                // its module, otherwise → return to the card's home module.
                const pin: Pin = c.projKey
                  ? { id: `project:${c.projKey}`, label: c.name, kind: 'project', key: c.projKey }
                  : c.nav
                    ? { id: `module:${c.nav}`, label: c.name, kind: 'module', key: c.nav }
                    : { id: `card:${moduleKey}:${c.name}`, label: c.name, kind: 'card', key: moduleKey };
                return (
                  <Card key={i} c={c} onNav={go} onCard={(card) => openDrill(cardDrill(card, m.label))} onProject={openProject}
                    pinned={isPinned(pin.id)} onPin={() => togglePin(pin)} />
                );
              })}
            </div>
            )}
          </div>
        ) : tab === 'Workflow' ? (
          <WorkflowList workflows={tabWorkflows} onOpen={(w) => openDrill(workflowDrill(w))}
            renderPin={(w) => itemPin(`wf:${moduleKey}:${w.name}`, w.name, 'Workflow')} />
        ) : suppliedPanel ? (
          // Renders the SUPPLIED panel. This read `TAB_PANELS[moduleKey][tab]`
          // until 2026-09-01 — the seam's panel was used as a truthiness test
          // and then thrown away, so a supplier's rows could never appear and
          // the static demo rows always did.
          <TabPanelView panel={suppliedPanel} onRow={(r) => openDrill(r.thread ? messageDrill(r, `${m.label} · ${tab}`) : rowDrill(r, `${m.label} · ${tab}`))}
            renderPin={(r) => itemPin(`row:${moduleKey}:${tab}:${r.title}`, r.title, tab)} />
        ) : (
          <div className="kd-note" style={{ marginTop: 24 }}>
            <b>{tab}</b> — this domain layer is part of the {m.label} module.
          </div>
        )}
          </>
        )}
      </main>

      {/* ask kathedral — docked right, conversational, collapsible (N75) */}
      <AskPanel open={askOpen} onToggle={toggleAsk} />

      {drill && (
        <Drill
          def={drill}
          // ui_bug3: the ✕ / Escape / scrim / header-back are a FULL EXIT (close
          // the whole drill stack), NOT a one-level back button. Popping ONE level
          // (nested → parent) is the OS-back gesture's job (useLayerHistory below).
          onClose={clearDrills}
          onNavigate={(nav) => {
            clearDrills();
            if (nav.kind === 'project') openProject(nav.key);
            else go(nav.key as ModuleKey);
          }}
          onPrev={drill.kind === 'milestone' && drill.ctx && drill.ctx.idx > 0
            ? () => replaceDrill(milestoneDrill(DEALS[drill.ctx!.deal], PHASES, drill.ctx!.idx - 1)) : undefined}
          onNext={drill.kind === 'milestone' && drill.ctx && drill.ctx.idx < PHASES.length - 1
            ? () => replaceDrill(milestoneDrill(DEALS[drill.ctx!.deal], PHASES, drill.ctx!.idx + 1)) : undefined}
          onStep={drill.kind === 'milestone' && drill.ctx
            ? (i) => pushDrill(milestoneDrill(DEALS[drill.ctx!.deal], PHASES, i)) : undefined}
        />
      )}
    </div>
    </TemplateDndProvider>
  );
}
